/**
 * Shows the pictures a campaign will send.
 *
 * A message embeds its images as inline attachments, so the copy carries
 * `src="cid:xNNN"` — which means something to a mail client and nothing to a
 * browser. The editor and every preview still have to show the picture, and an
 * `<img>` tag cannot carry a bearer token, so the bytes are fetched the way
 * everything else is and turned into an object URL.
 *
 * One fetch per image for the life of the tab: a preview that re-renders on
 * every keystroke must not re-download a photograph each time.
 */
import { useEffect, useState } from 'react';
import api from './api.js';
import { isOffline } from './storage.js';

const CID = /cid:(x[0-9a-f]{32})/g;

/** cid → object URL, once fetched. Shared across every screen in the tab. */
const cache = new Map();
const inFlight = new Map();

/** The asset id inside a cid, which is the uuid with its dashes taken out. */
const idOf = cid => {
  const hex = cid.slice(1);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-`
    + `${hex.slice(16, 20)}-${hex.slice(20)}`;
};

function load(cid) {
  if (cache.has(cid)) return Promise.resolve(cache.get(cid));
  if (inFlight.has(cid)) return inFlight.get(cid);

  const job = api.marketing.assetBlob(idOf(cid))
    .then(blob => {
      const url = URL.createObjectURL(blob);
      cache.set(cid, url);
      return url;
    })
    .catch(() => {
      // A missing image should leave a gap, not break the preview.
      cache.set(cid, '');
      return '';
    })
    .finally(() => inFlight.delete(cid));

  inFlight.set(cid, job);
  return job;
}

/** Remember a picture the browser already holds, so it needs no fetch. */
export const seedInlineImage = (cid, url) => { if (cid && url) cache.set(cid, url); };

/** Swap every `cid:` for something a browser can draw. */
export function withInlineImages(html, urls) {
  return String(html || '').replace(CID, (whole, cid) => urls[cid] || whole);
}

/**
 * Display-ready copy of `html`.
 *
 * Returns the original until the pictures are in, so text is never held back
 * waiting on an image.
 */
export function useInlineImages(html) {
  const [urls, setUrls] = useState({});

  /* The cids actually mentioned, as a stable string, so the effect does not
     re-run for every character typed around them. */
  const cids = [...new Set(String(html || '').match(CID) || [])]
    .map(x => x.slice(4))
    .sort()
    .join(',');

  useEffect(() => {
    if (!cids || isOffline()) return undefined;

    let alive = true;
    Promise.all(cids.split(',').map(cid => load(cid).then(url => [cid, url])))
      .then(pairs => {
        if (!alive) return;
        setUrls(was => {
          const next = { ...was };
          pairs.forEach(([cid, url]) => { if (url) next[cid] = url; });
          return next;
        });
      });

    return () => { alive = false; };
  }, [cids]);

  return withInlineImages(html, urls);
}
